// ═══════════════════════════════════════════════════════════════
// PIXALLOY — Camera RAW Develop Processor (Phase 10)
// ═══════════════════════════════════════════════════════════════
// Applies a non-destructive develop pipeline to ImageData.
// Pipeline order: WB → Exposure → Contrast → Highlights/Shadows →
// Whites/Blacks → HSL → Split Toning → Tone Curve → Detail → Effects
// ═══════════════════════════════════════════════════════════════

// Default develop settings
export const DEV_DEFAULTS = {
  // Basic
  exposure: 0,        // -5 to +5 EV
  contrast: 0,        // -100 to +100
  highlights: 0,      // -100 to +100
  shadows: 0,         // -100 to +100
  whites: 0,          // -100 to +100
  blacks: 0,          // -100 to +100
  // White Balance
  temperature: 0,     // -100 (cool) to +100 (warm)
  tint: 0,            // -100 (green) to +100 (magenta)
  // Tone Curve (applied after basic)
  curvePoints: [[0,0],[64,64],[128,128],[192,192],[255,255]],
  // HSL (per-hue adjustments)
  hslHue: {},         // e.g. {reds:0, oranges:0, yellows:0, ...}
  hslSat: {},
  hslLum: {},
  // Split Toning
  splitHighHue: 0,    // 0-360
  splitHighSat: 0,    // 0-100
  splitShadowHue: 0,
  splitShadowSat: 0,
  splitBalance: 0,    // -100 to +100
  // Detail
  sharpenAmount: 0,   // 0-150
  sharpenRadius: 1,   // 0.5-3
  noiseReduction: 0,  // 0-100
  // Effects
  dehaze: 0,          // -100 to +100
  grainAmount: 0,     // 0-100
  vignetteAmount: 0,  // -100 to +100
};

const HSL_RANGES = ["reds","oranges","yellows","greens","aquas","blues","purples","magentas"];
const HSL_HUE_CENTERS = [0, 30, 60, 120, 180, 240, 280, 320]; // degrees
const HSL_HUE_WIDTH = 30; // ±degrees

// Helper: RGB ↔ HSL
function rgb2hsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), dl = mx - mn;
  let h = 0, s = 0; const l = (mx + mn) / 2;
  if (dl > 0) {
    s = l > 0.5 ? dl / (2 - mx - mn) : dl / (mx + mn);
    if (mx === r) h = ((g - b) / dl + (g < b ? 6 : 0)) / 6;
    else if (mx === g) h = ((b - r) / dl + 2) / 6;
    else h = ((r - g) / dl + 4) / 6;
  }
  return [h * 360, s, l];
}

function hsl2rgb(h, s, l) {
  h /= 360;
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1/6) return p + (q-p)*6*t; if (t < 1/2) return q; if (t < 2/3) return p + (q-p)*(2/3-t)*6; return p; };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
  return [Math.round(hue2rgb(p, q, h + 1/3) * 255), Math.round(hue2rgb(p, q, h) * 255), Math.round(hue2rgb(p, q, h - 1/3) * 255)];
}

function clamp(v) { return v < 0 ? 0 : v > 255 ? 255 : v; }

/**
 * Apply the full develop pipeline to ImageData in-place.
 */
export function applyDevelop(id, params) {
  const d = id.data, len = d.length, w = id.width, h = id.height;
  const p = { ...DEV_DEFAULTS, ...params };

  // ── 1. White Balance (temperature/tint shift) ──
  if (p.temperature !== 0 || p.tint !== 0) {
    const tShift = p.temperature / 100; // -1 to +1
    const mShift = p.tint / 100;
    for (let i = 0; i < len; i += 4) {
      if (d[i+3] < 1) continue;
      d[i]   = clamp(d[i]   + tShift * 30);   // warm = +red
      d[i+2] = clamp(d[i+2] - tShift * 30);   // warm = -blue
      d[i+1] = clamp(d[i+1] - mShift * 20);   // tint = -green
      d[i]   = clamp(d[i]   + mShift * 10);    // tint = +red slight
    }
  }

  // ── 2. Exposure (EV-based, multiplicative) ──
  if (p.exposure !== 0) {
    const mult = Math.pow(2, p.exposure);
    for (let i = 0; i < len; i += 4) {
      if (d[i+3] < 1) continue;
      d[i]   = clamp(Math.round(d[i]   * mult));
      d[i+1] = clamp(Math.round(d[i+1] * mult));
      d[i+2] = clamp(Math.round(d[i+2] * mult));
    }
  }

  // ── 3. Contrast (S-curve around midpoint) ──
  if (p.contrast !== 0) {
    const ct = p.contrast / 100;
    const factor = (1 + ct) * (1 + ct);
    const lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) { let v = (i / 255 - 0.5) * factor + 0.5; lut[i] = clamp(Math.round(v * 255)); }
    for (let i = 0; i < len; i += 4) { d[i] = lut[d[i]]; d[i+1] = lut[d[i+1]]; d[i+2] = lut[d[i+2]]; }
  }

  // ── 4. Highlights / Shadows / Whites / Blacks ──
  if (p.highlights !== 0 || p.shadows !== 0 || p.whites !== 0 || p.blacks !== 0) {
    for (let i = 0; i < len; i += 4) {
      if (d[i+3] < 1) continue;
      const lum = d[i] * 0.299 + d[i+1] * 0.587 + d[i+2] * 0.114;
      let adj = 0;
      // Shadows: boost dark pixels
      if (lum < 85 && p.shadows !== 0) adj += (p.shadows / 100) * (85 - lum) / 85 * 80;
      // Highlights: reduce bright pixels
      if (lum > 170 && p.highlights !== 0) adj -= (p.highlights / 100) * (lum - 170) / 85 * 80;
      // Blacks: crush or lift the very dark end
      if (lum < 40 && p.blacks !== 0) adj += (p.blacks / 100) * (40 - lum) / 40 * 40;
      // Whites: push or pull the very bright end
      if (lum > 215 && p.whites !== 0) adj += (p.whites / 100) * (lum - 215) / 40 * 40;
      if (adj !== 0) {
        d[i]   = clamp(Math.round(d[i]   + adj));
        d[i+1] = clamp(Math.round(d[i+1] + adj));
        d[i+2] = clamp(Math.round(d[i+2] + adj));
      }
    }
  }

  // ── 5. HSL adjustments (per-hue hue/saturation/luminance shifts) ──
  const hasHSL = HSL_RANGES.some(r => (p.hslHue[r]||0) !== 0 || (p.hslSat[r]||0) !== 0 || (p.hslLum[r]||0) !== 0);
  if (hasHSL) {
    for (let i = 0; i < len; i += 4) {
      if (d[i+3] < 1) continue;
      let [h2, s2, l2] = rgb2hsl(d[i], d[i+1], d[i+2]);
      for (let ri = 0; ri < HSL_RANGES.length; ri++) {
        const rn = HSL_RANGES[ri];
        const hAdj = p.hslHue[rn] || 0, sAdj = p.hslSat[rn] || 0, lAdj = p.hslLum[rn] || 0;
        if (hAdj === 0 && sAdj === 0 && lAdj === 0) continue;
        // Calculate hue distance (wrapping)
        let dh = Math.abs(h2 - HSL_HUE_CENTERS[ri]);
        if (dh > 180) dh = 360 - dh;
        if (dh > HSL_HUE_WIDTH) continue;
        const weight = 1 - dh / HSL_HUE_WIDTH; // 1 at center, 0 at edge
        h2 += hAdj * weight;
        s2 = Math.max(0, Math.min(1, s2 + (sAdj / 100) * weight));
        l2 = Math.max(0, Math.min(1, l2 + (lAdj / 100) * 0.3 * weight));
      }
      if (h2 < 0) h2 += 360; if (h2 >= 360) h2 -= 360;
      const [nr, ng, nb] = hsl2rgb(h2, s2, l2);
      d[i] = nr; d[i+1] = ng; d[i+2] = nb;
    }
  }

  // ── 6. Split Toning ──
  if (p.splitHighSat > 0 || p.splitShadowSat > 0) {
    const [hhr, hhg, hhb] = hsl2rgb(p.splitHighHue, p.splitHighSat / 100, 0.5);
    const [shr, shg, shb] = hsl2rgb(p.splitShadowHue, p.splitShadowSat / 100, 0.5);
    const bal = (p.splitBalance + 100) / 200; // 0 = all shadow, 1 = all highlight
    for (let i = 0; i < len; i += 4) {
      if (d[i+3] < 1) continue;
      const lum = (d[i] * 0.299 + d[i+1] * 0.587 + d[i+2] * 0.114) / 255;
      // Blend: shadows get shadow tint, highlights get highlight tint
      const hw = Math.max(0, (lum - bal) / (1 - bal + 0.001)); // highlight weight
      const sw = Math.max(0, (bal - lum) / (bal + 0.001));      // shadow weight
      const hStr = hw * (p.splitHighSat / 100) * 0.3;
      const sStr = sw * (p.splitShadowSat / 100) * 0.3;
      d[i]   = clamp(Math.round(d[i]   + (hhr - 128) * hStr + (shr - 128) * sStr));
      d[i+1] = clamp(Math.round(d[i+1] + (hhg - 128) * hStr + (shg - 128) * sStr));
      d[i+2] = clamp(Math.round(d[i+2] + (hhb - 128) * hStr + (shb - 128) * sStr));
    }
  }

  // ── 7. Tone Curve ──
  if (p.curvePoints && p.curvePoints.length > 2) {
    // Simple linear interpolation LUT
    const pts = p.curvePoints.slice().sort((a, b) => a[0] - b[0]);
    const lut = new Uint8Array(256);
    for (let x = 0; x < 256; x++) {
      let lo = pts[0], hi = pts[pts.length - 1];
      for (let j = 0; j < pts.length - 1; j++) {
        if (x >= pts[j][0] && x <= pts[j+1][0]) { lo = pts[j]; hi = pts[j+1]; break; }
      }
      const t = hi[0] === lo[0] ? 0 : (x - lo[0]) / (hi[0] - lo[0]);
      lut[x] = clamp(Math.round(lo[1] + t * (hi[1] - lo[1])));
    }
    // Only apply if not identity
    let isIdentity = true;
    for (let i = 0; i < 256; i++) { if (lut[i] !== i) { isIdentity = false; break; } }
    if (!isIdentity) {
      for (let i = 0; i < len; i += 4) { d[i] = lut[d[i]]; d[i+1] = lut[d[i+1]]; d[i+2] = lut[d[i+2]]; }
    }
  }

  // ── 8. Sharpen (unsharp mask) ──
  if (p.sharpenAmount > 0) {
    const amt = p.sharpenAmount / 100;
    const r2 = Math.max(1, Math.round(p.sharpenRadius));
    // Simple 3x3 unsharp: original + amt*(original - blurred)
    const orig = new Uint8ClampedArray(d);
    for (let y = r2; y < h - r2; y++) for (let x = r2; x < w - r2; x++) {
      const pi = (y * w + x) * 4;
      if (d[pi+3] < 1) continue;
      // Average neighbors
      let sr = 0, sg = 0, sb = 0, cnt = 0;
      for (let dy = -r2; dy <= r2; dy++) for (let dx = -r2; dx <= r2; dx++) {
        const ni = ((y+dy) * w + (x+dx)) * 4;
        sr += orig[ni]; sg += orig[ni+1]; sb += orig[ni+2]; cnt++;
      }
      sr /= cnt; sg /= cnt; sb /= cnt;
      d[pi]   = clamp(Math.round(orig[pi]   + (orig[pi]   - sr) * amt));
      d[pi+1] = clamp(Math.round(orig[pi+1] + (orig[pi+1] - sg) * amt));
      d[pi+2] = clamp(Math.round(orig[pi+2] + (orig[pi+2] - sb) * amt));
    }
  }

  // ── 9. Noise Reduction (simple box blur on low-contrast areas) ──
  if (p.noiseReduction > 0) {
    const str = p.noiseReduction / 100;
    const orig = new Uint8ClampedArray(d);
    const r2 = 1;
    for (let y = r2; y < h - r2; y++) for (let x = r2; x < w - r2; x++) {
      const pi = (y * w + x) * 4;
      if (d[pi+3] < 1) continue;
      let sr = 0, sg = 0, sb = 0, cnt = 0;
      for (let dy = -r2; dy <= r2; dy++) for (let dx = -r2; dx <= r2; dx++) {
        const ni = ((y+dy) * w + (x+dx)) * 4;
        sr += orig[ni]; sg += orig[ni+1]; sb += orig[ni+2]; cnt++;
      }
      sr /= cnt; sg /= cnt; sb /= cnt;
      d[pi]   = clamp(Math.round(orig[pi]   * (1 - str) + sr * str));
      d[pi+1] = clamp(Math.round(orig[pi+1] * (1 - str) + sg * str));
      d[pi+2] = clamp(Math.round(orig[pi+2] * (1 - str) + sb * str));
    }
  }

  // ── 10. Dehaze ──
  if (p.dehaze !== 0) {
    const str = p.dehaze / 100;
    for (let i = 0; i < len; i += 4) {
      if (d[i+3] < 1) continue;
      const mn = Math.min(d[i], d[i+1], d[i+2]);
      const boost = str * mn * 0.5;
      d[i]   = clamp(Math.round(d[i]   - boost + str * 15));
      d[i+1] = clamp(Math.round(d[i+1] - boost + str * 15));
      d[i+2] = clamp(Math.round(d[i+2] - boost + str * 15));
    }
  }

  // ── 11. Grain ──
  if (p.grainAmount > 0) {
    const amt = p.grainAmount / 100 * 40;
    for (let i = 0; i < len; i += 4) {
      if (d[i+3] < 1) continue;
      const noise = (Math.random() - 0.5) * amt;
      d[i]   = clamp(Math.round(d[i]   + noise));
      d[i+1] = clamp(Math.round(d[i+1] + noise));
      d[i+2] = clamp(Math.round(d[i+2] + noise));
    }
  }

  // ── 12. Vignette ──
  if (p.vignetteAmount !== 0) {
    const str = p.vignetteAmount / 100;
    const cx = w / 2, cy = h / 2;
    const maxDist = Math.sqrt(cx * cx + cy * cy);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const dx = x - cx, dy = y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy) / maxDist;
      const falloff = Math.max(0, dist - 0.3) / 0.7; // starts at 30% from center
      const darken = falloff * falloff * str * -128;
      if (darken === 0) continue;
      const pi = (y * w + x) * 4;
      d[pi]   = clamp(Math.round(d[pi]   + darken));
      d[pi+1] = clamp(Math.round(d[pi+1] + darken));
      d[pi+2] = clamp(Math.round(d[pi+2] + darken));
    }
  }
}
